import { ViewController } from '@estee/elc-service';
import { action, computed, observable } from 'mobx';
import { StoresRepository } from '~repositories/StoresRepository';
import { FindInStore } from '~domain/entities/FindInStore';
import { StoreItemSelectedUseCase } from '~use-cases/StoreItemSelectedUseCase';
import { StoreInfoClickedUseCase } from '~use-cases/StoreInfoClickedUseCase';
import { StorePhoneClickedUseCase } from '~use-cases/StorePhoneClickedUseCase';
import { Config } from '~domain/entities/Config';
import { GoToDirectoryClickedUseCase } from '~use-cases/GoToDirectoryClickedUseCase';
import { LocationResetClickedUseCase } from '~use-cases/LocationResetClickedUseCase';
import { StoreDeliveryOptions } from '~constants/constants';
import { events, emit } from '@estee/elc-service-bus';

export class BopisStoresSidebarViewController extends ViewController {
    @observable private storesRepository: StoresRepository;
    @observable private findInStore: FindInStore;
    private configContainer: Config;

    constructor(storesRepository: StoresRepository, findInStore: FindInStore, config: Config) {
        super();
        this.storesRepository = storesRepository;
        this.findInStore = findInStore;
        this.configContainer = config;
    }

    @action
    public loaded = async () => {
        this.isLoading = false;
    };

    @action
    public onStoreSelected = async (storeId: string, storeName?: string) => {
        if (storeName) {
            emit(events.STORE_SELECTED, {
                eventAction: StoreDeliveryOptions.STORE_SELECTION,
                eventLabel: storeName
            });
        }
        await new StoreItemSelectedUseCase(storeId).execute();
    };

    @computed
    get currentStore() {
        return this.stores[0];
    }

    @computed
    get stores() {
        return this.storesRepository.stores;
    }

    @computed
    get filtersEnabled() {
        if (this.configContainer.config.featureFlags) {
            return this.configContainer.config.featureFlags.enableFilters;
        }

        return false;
    }

    @action
    public onPhoneClick = () => {
        const { id, name } = this.currentStore;
        new StorePhoneClickedUseCase(id, name).execute();
    };

    @action
    public onGoToDirectoryClick = () => {
        new GoToDirectoryClickedUseCase().execute();
    };

    @action
    public onStoreInfoLinkClick = () => {
        const { id, name } = this.currentStore;
        new StoreInfoClickedUseCase(id, name).execute();
    };

    public onLocationReset = () => {
        new LocationResetClickedUseCase().execute();
    };

    @computed
    public get data() {
        return {
            stores: this.stores,
            storeInventoryMap: this.storesRepository.storeInventory,
            findInStore: this.findInStore,
            areStoresLoading: this.storesRepository.isLoading,
            selectedStoreId: this.storesRepository.selectedStoreId,
            apiKey: this.configContainer.config.googleApiKey,
            mapRegion: this.configContainer.config.mapRegion,
            mapLanguage: this.configContainer.config.mapLanguage,
            filtersEnabled: this.filtersEnabled
        };
    }
}
